import { useEffect, useRef, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import type { DropdownItem } from "@/modules/dropdowns/types";
import type {
  CreateNotePayload,
  NoteComment,
  NoteDetail,
  NoteListItem,
  NoteRelated,
  PaginatedResponse,
  UpdateNotePayload,
} from "../types";

const NOTES_KEY = ["notes"] as const;

export type NoteListParams = {
  page?: number;
  limit?: number;
  search?: string;
  companyId?: string;
  productId?: string;
  projectId?: string;
  meetingId?: string;
  taskId?: string;
  actionId?: string;
  phaseId?: string;
  isPinned?: boolean;
  type?: string;
  status?: string;
  visibility?: string;
  tags?: string[];
};

export function useNoteList(
  params: NoteListParams = {},
): UseQueryResult<PaginatedResponse<NoteListItem>> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...NOTES_KEY, "list", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<PaginatedResponse<NoteListItem>>("/notes", {
        token: accessToken,
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          companyId: params.companyId,
          productId: params.productId,
          projectId: params.projectId,
          meetingId: params.meetingId,
          taskId: params.taskId,
          actionId: params.actionId,
          phaseId: params.phaseId,
          type: params.type,
          status: params.status,
          visibility: params.visibility,
          tags: params.tags && params.tags.length > 0 ? params.tags.join(",") : undefined,
          isPinned:
            typeof params.isPinned === "boolean"
              ? params.isPinned
                ? "true"
                : "false"
              : undefined,
        },
      }),
  });
}

export function useNote(
  id: string | null | undefined,
): UseQueryResult<NoteDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...NOTES_KEY, "detail", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ note: NoteDetail }>(`/notes/${id}`, {
        token: accessToken,
      });
      return res.note;
    },
  });
}

export function useNoteRelated(
  id: string | null | undefined,
): UseQueryResult<NoteRelated> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...NOTES_KEY, "related", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: () =>
      apiFetch<NoteRelated>(`/notes/${id}/related`, { token: accessToken }),
  });
}

export function useCreateNote() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateNotePayload) =>
      apiFetch<{ note: NoteDetail }>("/notes", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...NOTES_KEY] });
    },
  });
}

export function useUpdateNote(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateNotePayload) =>
      apiFetch<{ note: NoteDetail }>(`/notes/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...NOTES_KEY] });
    },
  });
}

export function useUpdateNoteContent(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { content: string; wordCount?: number }) =>
      apiFetch<{ note: NoteDetail }>(`/notes/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...NOTES_KEY, "detail", id] });
      qc.invalidateQueries({ queryKey: [...NOTES_KEY, "list"] });
    },
  });
}

export function useTogglePin() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<
    { note: NoteDetail },
    Error,
    { id: string; isPinned: boolean }
  >({
    mutationFn: ({ id, isPinned }) =>
      apiFetch<{ note: NoteDetail }>(`/notes/${id}/pin`, {
        method: "PUT",
        body: { isPinned },
        token: accessToken,
      }),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: [...NOTES_KEY] });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...NOTES_KEY] });
    },
  });
}

export function useDeleteNote() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/notes/${id}`, { method: "DELETE", token: accessToken }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...NOTES_KEY] });
    },
  });
}

export function useNoteComments(
  noteId: string | null | undefined,
): UseQueryResult<NoteComment[]> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...NOTES_KEY, "comments", noteId, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!noteId,
    queryFn: async () => {
      const res = await apiFetch<{ data: NoteComment[] }>(
        `/notes/${noteId}/comments`,
        { token: accessToken },
      );
      return res.data;
    },
  });
}

export function useAddNoteComment(noteId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      apiFetch<{ comment: NoteComment }>(`/notes/${noteId}/comments`, {
        method: "POST",
        body: { content },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...NOTES_KEY, "comments", noteId] });
    },
  });
}

export function useDeleteNoteComment(noteId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      apiFetch(`/notes/${noteId}/comments/${commentId}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...NOTES_KEY, "comments", noteId] });
    },
  });
}

export function useNoteTags(): UseQueryResult<DropdownItem[]> {
  return useCategoryItems("note_tag");
}

export function useNoteTypes(): UseQueryResult<DropdownItem[]> {
  return useCategoryItems("note_type");
}

export function useNoteStatuses(): UseQueryResult<DropdownItem[]> {
  return useCategoryItems("note_status");
}

type AutoSaveState = {
  isSaving: boolean;
  lastSaved: Date | null;
  isDirty: boolean;
};

export function useAutoSave(
  noteId: string,
  title: string,
  content: string,
  enabled: boolean,
): AutoSaveState & { saveNow: () => Promise<void> } {
  const update = useUpdateNote(noteId);
  const [state, setState] = useState<AutoSaveState>({
    isSaving: false,
    lastSaved: null,
    isDirty: false,
  });
  const initial = useRef<{ title: string; content: string } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (initial.current === null) {
      initial.current = { title, content };
      return;
    }
    const dirty =
      title !== initial.current.title || content !== initial.current.content;
    setState((s) => ({ ...s, isDirty: dirty }));
    if (!dirty) return;

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      void doSave();
    }, 2000);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, content, enabled]);

  useEffect(() => {
    if (!enabled) {
      initial.current = null;
      setState({ isSaving: false, lastSaved: null, isDirty: false });
    }
  }, [enabled]);

  async function doSave() {
    if (!noteId) return;
    setState((s) => ({ ...s, isSaving: true }));
    try {
      await update.mutateAsync({ title, content });
      initial.current = { title, content };
      setState({ isSaving: false, lastSaved: new Date(), isDirty: false });
    } catch {
      setState((s) => ({ ...s, isSaving: false }));
    }
  }

  async function saveNow() {
    if (timer.current) clearTimeout(timer.current);
    await doSave();
  }

  return { ...state, saveNow };
}
